import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { menuAPI, permissionsAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';

/**
 * MenuAccessRoute Component
 * Checks if user has access to a specific menu/page
 * Redirects to dashboard if access is denied
 */
const MenuAccessRoute = ({ children, menuName }) => {
  const { user } = useAuth();
  const [hasAccess, setHasAccess] = useState(null);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        // Admin users have access to everything
        const userRole = user?.role || user?.Role || '';
        if (userRole.toLowerCase() === 'admin' || user?.isAdmin) {
          setHasAccess(true);
          return;
        }

        // Get user permissions
        const permResponse = await permissionsAPI.getUserPermissions();
        const permissions = permResponse.data?.permissions || {};

        // Admin-only menus that regular clients cannot access
        const adminOnlyMenus = [
          'Manage Menu Items',
          'Menus',
          'MGB Setup',
          'Menu Management'
        ];

        if (adminOnlyMenus.includes(menuName)) {
          setHasAccess(false);
          return;
        }

        // For table-based menus, check if user has view permission
        if (menuName && menuName !== 'Dashboard' && menuName !== 'Tables') {
          // Check if the menu is linked to a table and user has permission
          const menuResponse = await menuAPI.getMenuHierarchy();
          if (menuResponse.status === 200) {
            const items = menuResponse.data.data || [];
            const menuItem = findMenuItemByName(items, menuName);
            
            if (menuItem && menuItem.LinkRecordID && menuItem.LinkRecordID !== -1) {
              try {
                const tableResponse = await menuAPI.getMenuItemTableName(menuItem.MenuItemID);
                if (tableResponse.status === 200) {
                  const tableName = tableResponse.data.tableName;
                  // Check view permission
                  if (permissions[tableName]?.CanView) {
                    setHasAccess(true);
                  } else {
                    setHasAccess(false);
                  }
                } else {
                  setHasAccess(false);
                }
              } catch (error) {
                setHasAccess(false);
              }
            } else {
              // Menu exists and doesn't require permission check
              setHasAccess(true);
            }
          } else {
            setHasAccess(false);
          }
        } else {
          // Dashboard and Tables are always accessible
          setHasAccess(true);
        }
      } catch (error) {
        setHasAccess(false);
      }
    };

    checkAccess();
  }, [menuName, user]);

  const findMenuItemByName = (items, name) => {
    for (const item of items) {
      if (item.Name === name) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = findMenuItemByName(item.children, name);
        if (found) return found;
      }
    }
    return null;
  };

  // Still loading
  if (hasAccess === null) {
    return null;
  }

  // Access denied, redirect to dashboard
  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  // Access granted, render component
  return children;
};

export default MenuAccessRoute;
